/**
 * Client-side image compression for Dialpad MMS.
 *
 * Dialpad's `media` field has a HARD limit of 500 KiB raw file size. Phone photos
 * are routinely 2-5 MB, so we downscale + re-encode to JPEG until the result fits
 * comfortably under the limit before uploading to Firebase Storage.
 *
 * Notes:
 * - Animated GIFs are NOT recompressed (canvas would flatten the animation); they
 *   are passed through unchanged and rejected if already over the limit.
 * - Small JPEG/PNG/WebP images already under the target are returned as-is so we
 *   don't needlessly degrade crisp screenshots.
 * - Everything else is re-encoded to JPEG on a white background.
 */

// Leave headroom under Dialpad's 500 KiB so we never round up over the wire.
export const TARGET_MAX_BYTES = 480 * 1024;
export const HARD_MAX_BYTES = 500 * 1024;
const MAX_DIMENSION = 1600;
const MIN_DIMENSION = 400;
const PASSTHROUGH_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

function formatKb(bytes) {
    return `${Math.round(bytes / 1024)} KB`;
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Could not read the selected image.'));
        };
        img.src = url;
    });
}

function drawToBlob(img, width, height, quality) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // Fill white so transparent PNGs don't turn black when flattened to JPEG.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
            (blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Image compression failed.'));
            },
            'image/jpeg',
            quality
        );
    });
}

/**
 * Compress/normalize an image File for MMS sending.
 *
 * @param {File} file - The user-selected image file.
 * @returns {Promise<File>} A File guaranteed to be <= 500 KiB (or throws).
 * @throws {Error} when the image cannot be read or cannot be compressed under the limit.
 */
export async function compressImageForMms(file) {
    if (!file) {
        throw new Error('No file selected.');
    }

    // Animated GIFs: do not recompress (would flatten animation). Size-check only.
    if (file.type === 'image/gif') {
        if (file.size > HARD_MAX_BYTES) {
            throw new Error(
                `Animated GIFs must be under 500 KB for MMS (this one is ${formatKb(file.size)}).`
            );
        }
        return file;
    }

    // Already small and a natively-supported MMS type: keep it as-is.
    if (PASSTHROUGH_TYPES.includes(file.type) && file.size <= TARGET_MAX_BYTES) {
        return file;
    }

    const img = await loadImage(file);

    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;

    if (!width || !height) {
        throw new Error('Could not determine image dimensions.');
    }

    // Initial downscale to the max dimension.
    const initialScale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    width = Math.max(1, Math.round(width * initialScale));
    height = Math.max(1, Math.round(height * initialScale));

    let quality = 0.9;
    let blob = await drawToBlob(img, width, height, quality);

    // Step 1: drop quality.
    while (blob.size > TARGET_MAX_BYTES && quality > 0.4) {
        quality = Math.round((quality - 0.1) * 10) / 10;
        blob = await drawToBlob(img, width, height, quality);
    }

    // Step 2: shrink dimensions if quality alone wasn't enough.
    while (blob.size > TARGET_MAX_BYTES && Math.max(width, height) > MIN_DIMENSION) {
        width = Math.max(1, Math.round(width * 0.85));
        height = Math.max(1, Math.round(height * 0.85));
        blob = await drawToBlob(img, width, height, quality);
    }

    if (blob.size > HARD_MAX_BYTES) {
        throw new Error(
            `Could not compress this image under 500 KB (got ${formatKb(blob.size)}). Please choose a smaller image.`
        );
    }

    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}

// ---------------------------------------------------------------------------
// FutureBot chat attachments
// ---------------------------------------------------------------------------

// Vertex AI caps images at ~7 MB. Unlike MMS we keep generous dimensions
// (Gemini reads fine detail); only oversized files get re-encoded.
export const CHAT_IMAGE_MAX_BYTES = 7 * 1024 * 1024;
const CHAT_IMAGE_TARGET_BYTES = 6.5 * 1024 * 1024; // headroom under the hard cap
const CHAT_MAX_DIMENSION = 3072;
const CHAT_MIN_DIMENSION = 800;

/**
 * Normalize an image File for a FutureBot chat attachment.
 *
 * - Supported types already under the cap pass through untouched.
 * - GIF/HEIC/HEIF are never re-encoded via canvas (animation loss / spotty
 *   decode support) — size-checked only; Gemini reads them natively.
 * - Everything else is downscaled/re-encoded to JPEG until it fits.
 *
 * @param {File} file - The user-selected image file.
 * @returns {Promise<File>} A File guaranteed to be <= 7 MB (or throws).
 */
export async function normalizeImageForChat(file) {
    if (!file) {
        throw new Error('No file selected.');
    }

    if (['image/gif', 'image/heic', 'image/heif'].includes(file.type)) {
        if (file.size > CHAT_IMAGE_MAX_BYTES) {
            throw new Error(
                `${file.name || 'This image'} is over 7 MB and this format can't be compressed here. Please use a smaller file.`
            );
        }
        return file;
    }

    if (PASSTHROUGH_TYPES.includes(file.type) && file.size <= CHAT_IMAGE_TARGET_BYTES) {
        return file;
    }

    const img = await loadImage(file);

    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;
    if (!width || !height) {
        throw new Error('Could not determine image dimensions.');
    }

    const initialScale = Math.min(1, CHAT_MAX_DIMENSION / Math.max(width, height));
    width = Math.max(1, Math.round(width * initialScale));
    height = Math.max(1, Math.round(height * initialScale));

    let quality = 0.85;
    let blob = await drawToBlob(img, width, height, quality);

    while (blob.size > CHAT_IMAGE_TARGET_BYTES && quality > 0.5) {
        quality = Math.round((quality - 0.1) * 10) / 10;
        blob = await drawToBlob(img, width, height, quality);
    }
    while (blob.size > CHAT_IMAGE_TARGET_BYTES && Math.max(width, height) > CHAT_MIN_DIMENSION) {
        width = Math.max(1, Math.round(width * 0.8));
        height = Math.max(1, Math.round(height * 0.8));
        blob = await drawToBlob(img, width, height, quality);
    }

    if (blob.size > CHAT_IMAGE_MAX_BYTES) {
        throw new Error(
            `Could not compress this image under 7 MB (got ${formatKb(blob.size)}). Please choose a smaller image.`
        );
    }

    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}

export default compressImageForMms;
